import { BarChart3, Bell, Box, CreditCard, Home, Percent, Settings, ShoppingBag, Users, HelpCircle, MessageSquare } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';

import { useUIStore } from '../estado/useUIStore.ts';
import { useUnreadCount } from '../features/notificaciones/useNotifications.ts';
import { useOrderEvents } from '../features/pedidos/useOrderEvents.ts';

const links = [
  { to: '/panel', label: 'Inicio', icon: Home },
  { to: '/panel/catalogo', label: 'Catálogo', icon: Box },
  { to: '/panel/promociones', label: 'Promociones', icon: Percent },
  { to: '/panel/pedidos', label: 'Pedidos', icon: ShoppingBag, showOrdersBadge: true },
  { to: '/panel/reportes', label: 'Reportes', icon: BarChart3 },
  { to: '/panel/clientes', label: 'Clientes', icon: Users },
  { to: '/panel/suscripciones', label: 'Suscripciones', icon: CreditCard },
  { to: '/panel/notificaciones', label: 'Notificaciones', icon: Bell, showNotificationsBadge: true },
  { to: '/panel/pagos', label: 'Métodos de Pago', icon: ShoppingBag },
  { to: '/panel/ajustes', label: 'Ajustes', icon: Settings },
];

export function Sidebar({ onOpenFeedback }) {
  const isOpen = useUIStore((state) => state.sidebarOpen);
  const closeSidebar = useUIStore((state) => state.closeSidebar);
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count || 0;
  
  // 🚀 Hook para pedidos nuevos
  const { newOrdersCount } = useOrderEvents();

  return (
    <>
      {/* Overlay para móviles (Z-INDEX 45: Para tapar la navbar que es z-40) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[45] bg-black/80 backdrop-blur-sm lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}
      
      <aside
        className={`
          /* BASE: Fijo siempre */
          /* Z-INDEX 50: Para estar POR ENCIMA de la Navbar (que es z-40) */
          fixed top-0 left-0 z-50 h-full w-64 bg-[#050505] border-r border-white/5 transition-transform duration-300 ease-out
          
          /* MÓVIL: Se esconde o se muestra según isOpen */
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          
          /* DESKTOP: Ajustes para escritorio */
          /* En desktop bajamos el z-index a 30 para que los dropdowns de la navbar pasen por encima si fuera necesario */
          lg:translate-x-0 lg:top-16 lg:h-[calc(100vh-4rem)] lg:z-30
          
          /* Sombras sutiles solo en móvil */
          shadow-[10px_0_30px_rgba(0,0,0,0.5)] lg:shadow-none
        `}
      >
        {/* Añadido pt-6 pb-4 para dar más aire arriba en móviles */}
        <div className="flex h-full flex-col pt-6 pb-4 overflow-y-auto">
          
          {/* Header del Sidebar */}
          <div className="pl-4 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
            Panel de la tienda
          </div>

          <nav className="flex flex-1 flex-col gap-2 px-2">
            {links.map((link) => {
              const Icon = link.icon;
              
              // Badge de pedidos nuevos
              const showOrdersBadge = link.showOrdersBadge && newOrdersCount > 0;
              const ordersCount = newOrdersCount;
              
              // Badge de notificaciones no leídas
              const showNotificationsBadge = link.showNotificationsBadge && unreadCount > 0;
              const notificationsCount = unreadCount;
              
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/panel'}
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-3 py-3 text-[10px] font-bold tracking-widest uppercase transition-all duration-300 ${
                      isActive 
                        ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.2)] translate-x-1' 
                        : 'text-white/50 hover:text-white hover:bg-white/5 hover:pl-4'
                    }`
                  }
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="flex-1">{link.label}</span>
                  
                  {/* Badge de pedidos nuevos */}
                  {showOrdersBadge && (
                    <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-[0_0_10px_rgba(239,68,68,0.6)] animate-pulse">
                      {ordersCount > 99 ? '99+' : ordersCount}
                    </span>
                  )}
                  
                  {/* Badge de notificaciones no leídas */}
                  {showNotificationsBadge && (
                    <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-1 text-[9px] font-bold text-white shadow-[0_0_10px_rgba(59,130,246,0.6)] animate-pulse">
                      {notificationsCount > 99 ? '99+' : notificationsCount}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {/* Separador - Solo móvil */}
            <div className="lg:hidden my-2 border-t border-white/10" />

            {/* Botón de Ayuda - Solo móvil */}
            <Link
              to="/panel/ayuda"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeSidebar}
              className="lg:hidden group flex items-center gap-3 rounded-xl px-3 py-3 text-[10px] font-bold tracking-widest uppercase transition-all duration-300 text-white/50 hover:text-white hover:bg-white/5 hover:pl-4"
            >
              <HelpCircle className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1">Ayuda</span>
            </Link>

            {/* Botón de Feedback - Solo móvil */}
            <button
              onClick={() => {
                onOpenFeedback?.();
                closeSidebar();
              }}
              className="lg:hidden group flex items-center gap-3 rounded-xl px-3 py-3 text-[10px] font-bold tracking-widest uppercase transition-all duration-300 text-white/50 hover:text-white hover:bg-white/5 hover:pl-4 w-full text-left"
            >
              <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1">Feedback</span>
            </button>
          </nav>
        </div>
      </aside>
    </>
  );
}
